import React, { useEffect, useRef, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'


// Sticky bottom proxy of the table's native horizontal scrollbar.
//
// Problem: mui-datatables (via BaseMUIDataTable / responsiveBase) renders its
// scrollable area with `overflow: auto; maxHeight: calc(100vh - 147px)`. The
// horizontal scrollbar lives at the bottom of THAT container. With long pages
// the user has to scroll all the way down to reach it.
//
// Solution: render a synthetic horizontal scrollbar in a portal, positioned
// `fixed; bottom: 0`, aligned with the table's `getBoundingClientRect()`. It
// only shows when:
//   - the table actually overflows horizontally,
//   - the bottom edge of the native scroll container is BELOW the viewport,
//   - the table is still partially in view.
//
// `scrollLeft` is mirrored bidirectionally with a re-entrance guard.
// Portal escapes ancestor `transform`/`overflow: hidden` which can break
// `position: fixed`. Recomputed on window scroll/resize (rAF-throttled) and
// when the table or its content change size (`ResizeObserver`).
export default function StickyBottomScrollbar({ children }) {
    const wrapRef = useRef(null)
    const tableScrollRef = useRef(null)
    const proxyRef = useRef(null)
    const isSyncingRef = useRef(false)
    const rafRef = useRef(null)
    const cleanupRef = useRef(null)

    const [geom, setGeom] = useState({
        visible: false,
        left: 0,
        width: 0,
        scrollWidth: 0,
    })

    const findTableScroll = useCallback(() => {
        const root = wrapRef.current
        if (!root) return null
        return (
            root.querySelector('div[class*="MUIDataTable-responsiveBase"]') ||
            root.querySelector('.MuiTable-root')?.parentElement ||
            null
        )
    }, [])

    const compute = useCallback(() => {
        const el = tableScrollRef.current
        if (!el) return
        const rect = el.getBoundingClientRect()
        const viewportH = window.innerHeight || document.documentElement.clientHeight
        const tableInView = rect.top < viewportH && rect.bottom > 0
        const nativeBarOffscreen = rect.bottom > viewportH
        const needsHScroll = el.scrollWidth > el.clientWidth + 1
        const visible = needsHScroll && tableInView && nativeBarOffscreen
        setGeom(prev => {
            if (
                prev.visible === visible &&
                prev.left === rect.left &&
                prev.width === rect.width &&
                prev.scrollWidth === el.scrollWidth
            ) {
                return prev
            }
            return { visible, left: rect.left, width: rect.width, scrollWidth: el.scrollWidth }
        })
    }, [])

    const scheduleCompute = useCallback(() => {
        if (rafRef.current) return
        rafRef.current = requestAnimationFrame(() => {
            rafRef.current = null
            compute()
        })
    }, [compute])

    useEffect(() => {
        let cancelled = false
        let attempts = 0
        const MAX_ATTEMPTS = 40   // ~2s

        const attach = () => {
            if (cancelled) return
            const tableScroll = findTableScroll()
            if (!tableScroll) {
                if (attempts++ < MAX_ATTEMPTS) setTimeout(attach, 50)
                return
            }
            tableScrollRef.current = tableScroll

            const onTableScroll = () => {
                if (isSyncingRef.current) {
                    isSyncingRef.current = false
                    return
                }
                const proxy = proxyRef.current
                if (proxy) {
                    isSyncingRef.current = true
                    proxy.scrollLeft = tableScroll.scrollLeft
                }
            }
            tableScroll.addEventListener('scroll', onTableScroll, { passive: true })
            window.addEventListener('scroll', scheduleCompute, { passive: true, capture: true })
            window.addEventListener('resize', scheduleCompute)

            const ro = new ResizeObserver(scheduleCompute)
            ro.observe(tableScroll)
            const tableEl = tableScroll.querySelector('.MuiTable-root')
            if (tableEl) ro.observe(tableEl)

            scheduleCompute()

            cleanupRef.current = () => {
                tableScroll.removeEventListener('scroll', onTableScroll)
                window.removeEventListener('scroll', scheduleCompute, { capture: true })
                window.removeEventListener('resize', scheduleCompute)
                ro.disconnect()
                if (rafRef.current) cancelAnimationFrame(rafRef.current)
            }
        }

        attach()
        return () => {
            cancelled = true
            if (cleanupRef.current) cleanupRef.current()
        }
    }, [findTableScroll, scheduleCompute])

    // First mount of the proxy: align its scrollLeft with the table.
    useEffect(() => {
        if (geom.visible && proxyRef.current && tableScrollRef.current) {
            proxyRef.current.scrollLeft = tableScrollRef.current.scrollLeft
        }
    }, [geom.visible])

    const onProxyScroll = (e) => {
        const tableScroll = tableScrollRef.current
        if (!tableScroll) return
        if (isSyncingRef.current) {
            isSyncingRef.current = false
            return
        }
        isSyncingRef.current = true
        tableScroll.scrollLeft = e.target.scrollLeft
    }

    const proxy = geom.visible
        ? createPortal(
              <div
                  style={{
                      position: 'fixed',
                      bottom: 0,
                      left: geom.left,
                      width: geom.width,
                      paddingBottom: 8,
                      background: '#fff',
                      borderTop: '1px solid #E5E7EB',
                      boxShadow: '0 -2px 6px rgba(0, 0, 0, 0.06)',
                      zIndex: 1200,
                  }}
              >
                  <div
                      ref={proxyRef}
                      onScroll={onProxyScroll}
                      style={{
                          overflowX: 'auto',
                          overflowY: 'hidden',
                          height: 14,
                      }}
                  >
                      <div style={{ width: geom.scrollWidth, height: 1 }} />
                  </div>
              </div>,
              document.body
          )
        : null

    return <div ref={wrapRef}>
        {children}
        {proxy}
    </div>
}
